import { FieldType } from 'checkout/enums/FieldType';
import { COUNTRY_ISO_CODES } from 'checkout/enums/CountryIsoCodes';
import { IAddressFormField } from 'checkout/types/AddressFormField';
import { t } from 'localization/utils/t';

const TP = 'checkout.utils.getCountrySchemaFields';

type getCountrySchemaFieldsProps = {
  isPostalCodeRequired: boolean;
  country: COUNTRY_ISO_CODES;
};

export const getCountrySchemaFields = ({
  isPostalCodeRequired = true,
  country = COUNTRY_ISO_CODES.UNITED_STATES,
}: getCountrySchemaFieldsProps): Array<IAddressFormField | IAddressFormField[]> => {
  if (!country) return [];

  const STREET_ADDRESS = {
    type: FieldType.StreetAddress,
    label: t(`${TP}.addressLine1Label`, 'Street Address'),
    name: 'address1',
    'data-qa': 'checkout_shipping_address1',
  };

  const UNIT_NUMBER = {
    type: FieldType.UnitNumber,
    label: t(`${TP}.addressLine2Label`, 'Street Address 2 (optional)'),
    name: 'address2',
    'data-qa': 'checkout_shipping_address2',
  };

  const CITY = {
    type: FieldType.City,
    label: t(`${TP}.addressCityLabel`, 'City'),
    name: 'city',
    'data-qa': 'checkout_shipping_city',
  };

  const STATE = {
    type: FieldType.State,
    countryIsoCode: country,
    'data-qa': 'checkout_shipping_state_selector_button',
  };

  const POSTAL_CODE = {
    type: FieldType.PostalCode,
    label: isPostalCodeRequired
      ? t(`${TP}.addressPostalCodeLabel`, 'Postal / Zip Code')
      : t(`${TP}.addressPostalCodeOptionalLabel`, 'Postal / Zip Code (optional)'),
    name: 'postalCode',
    'data-qa': 'checkout_shipping_postal_code',
  };

  const PHONE = {
    type: FieldType.Phone,
    label: t(`${TP}.phoneNumberLabel`, 'Phone Number'),
    name: 'phone',
    'data-qa': 'checkout_shipping_phone',
  };

  const defaultCountrySchema = [STREET_ADDRESS, UNIT_NUMBER, [CITY, STATE], [POSTAL_CODE, PHONE]];

  const countrySchemas = {
    [COUNTRY_ISO_CODES.UNITED_STATES]: [
      STREET_ADDRESS,
      UNIT_NUMBER,
      [
        CITY,
        {
          ...STATE,
          countryIsoCode: COUNTRY_ISO_CODES.UNITED_STATES,
          label: t(`${TP}.stateDropdownLabelUS`, 'State'),
        },
      ],
      [POSTAL_CODE, PHONE],
    ],
    [COUNTRY_ISO_CODES.AUSTRALIA]: [
      STREET_ADDRESS,
      UNIT_NUMBER,
      [
        CITY,
        {
          ...STATE,
          countryIsoCode: COUNTRY_ISO_CODES.AUSTRALIA,
          label: t(`${TP}.stateDropdownLabelAU`, 'State'),
        },
      ],
      [POSTAL_CODE, PHONE],
    ],
    [COUNTRY_ISO_CODES.UNITED_KINGDOM]: [
      STREET_ADDRESS,
      UNIT_NUMBER,
      [
        CITY,
        {
          ...STATE,
          countryIsoCode: COUNTRY_ISO_CODES.UNITED_KINGDOM,
          label: t(`${TP}.stateDropdownLabelUK`, 'County'),
        },
      ],
      [POSTAL_CODE, PHONE],
    ],
    [COUNTRY_ISO_CODES.CANADA]: [
      STREET_ADDRESS,
      UNIT_NUMBER,
      [
        CITY,
        {
          ...STATE,
          countryIsoCode: COUNTRY_ISO_CODES.CANADA,
          label: t(`${TP}.stateDropdownLabelCA`, 'Province or Territory'),
        },
      ],
      [POSTAL_CODE, PHONE],
    ],
  };

  return countrySchemas[country] || defaultCountrySchema;
};
